import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.prefill) setMessage(e.detail.prefill);
      setOpen(true);
    };
    window.addEventListener("sq:open-feedback", handler);
    return () => window.removeEventListener("sq:open-feedback", handler);
  }, []);

  const send = async () => {
    if (message.trim().length < 3) { toast.error("Tell me a bit more about what you need 🙂"); return; }
    setSending(true);
    try {
      await api.post("/feedback", { message: `[REQUEST] ${message}`, email });
      toast.success("On it — I'll track that down and add it. Thanks for asking!");
      setMessage(""); setEmail(""); setOpen(false);
    } catch { toast.error("Couldn't send just now. Try again in a moment."); }
    setSending(false);
  };

  return (
    <>
      <button data-testid="feedback-open" onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-1.5 rounded-full bg-[#0F1420]/90 backdrop-blur border border-[#232D42] hover:border-[#00E5A3]/60 text-slate-300 hover:text-[#00E5A3] text-xs font-medium px-3 py-2 shadow-lg transition-colors">
        <Search size={14} className="text-[#00E5A3]" /> Can't find it?
      </button>

      {open && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-end sm:justify-end p-4"
          onClick={() => setOpen(false)}>
          <div data-testid="feedback-panel" onClick={(e) => e.stopPropagation()}
            className="w-full sm:w-[370px] rounded-2xl border border-[#232D42] bg-[#0F1420] p-5 shadow-2xl sm:mr-2 sm:mb-16">
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-heading font-bold text-slate-100 flex items-center gap-1.5">
                <Search size={16} className="text-[#00E5A3]" /> Looking for something?
              </h3>
              <button data-testid="feedback-close" onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-300"><X size={16} /></button>
            </div>
            <p className="text-xs text-slate-400 mb-3 leading-relaxed">
              Don't see the stat, player, matchup or piece of info you're after? Tell me what you need and
              <span className="text-slate-200"> I'll track it down and add it</span> — no charge, that's the whole point.
            </p>
            <textarea data-testid="feedback-message" value={message} onChange={(e) => setMessage(e.target.value)}
              rows={3} placeholder="e.g. “Add faceoff win % for centers” or “I can't find a certain player”"
              className="w-full mb-2 bg-[#151B2B] border border-[#232D42] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-[#00E5A3]/50 resize-none" />
            <input data-testid="feedback-email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email (optional, so I can tell you when it's live)"
              className="w-full mb-3 bg-[#151B2B] border border-[#232D42] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-[#00E5A3]/50" />
            <button data-testid="feedback-send" onClick={send} disabled={sending}
              className="w-full h-10 rounded-lg bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold disabled:opacity-60">
              {sending ? "Sending…" : "Find it for me"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

